/**
 * HTML sanitization for auto-redline preview markup.
 *
 * The backend builds preview HTML (`previewText`, `numberingPreview`) by
 * interpolating document-derived text — paragraph run text AND Word numbering
 * labels — into `<s>`/`<u>` redline markup, which the frontend renders via
 * `dangerouslySetInnerHTML`. That text is attacker-influenced: a maliciously
 * authored .docx (e.g. a numbering definition whose `w:lvlText` is
 * `<img src=x onerror=…>`) could otherwise inject script into the task pane,
 * which holds the Office SSO token. Every such string MUST be routed through
 * `sanitizeRedlineHtml` before it reaches a `dangerouslySetInnerHTML` sink.
 *
 * The allowlist is deliberately narrow — only the inline redline tags and the
 * light formatting the backend markdown formatter emits (it converts
 * `<deleted>`/`<inserted>`/`<comment>` to `<s>`/`<u>`/plain text before output,
 * so those never reach us), plus `style` for the inline redline colors
 * (DOMPurify sanitizes the CSS). Scripts, event handlers, `<img>`/remote-resource
 * tags, and any unknown element are stripped.
 */
import DOMPurify from "dompurify";

const ALLOWED_TAGS = ["s", "u", "b", "i", "em", "strong", "span", "p", "br", "hr"];
const ALLOWED_ATTR = ["style"];

/**
 * Sanitize backend-provided redline preview HTML for safe rendering via
 * `dangerouslySetInnerHTML`. Preserves the `<s>/<u>` + inline-color redline
 * convention while removing anything executable or resource-loading.
 */
export function sanitizeRedlineHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}
